/**
 * process-passes.ts — pure derivation of agent-process pass structure from
 * the raw session event stream.
 *
 * Process-mode runs emit `process_pass_started` {pass} and
 * `process_evaluation` {pass, met, critique_preview} events. This module correlates those events to a reasoning run by time
 * window and partitions the run's progress entries (steps + root nodes) into
 * per-pass groups for the progress tree.
 *
 * Legacy / single-pass runs produce no process events → extractRunPasses
 * returns [] and the caller renders exactly today's ungrouped view.
 *
 * No React imports — unit-testable pure functions.
 */

import type { SessionEvent, ProgressTree, ProgressStep, ProgressNode } from '@localagent/sdk';

export interface ProcessPassGroup {
  pass: number;
  startedAt: string;
  evaluation?: PassEvaluation;
}

export type PassEvaluation = { kind: 'verdict'; met: boolean; critiquePreview?: string };

function passEvaluation(e: SessionEvent): PassEvaluation {
  if (typeof e.data.met !== 'boolean') {
    throw new Error(`process_evaluation ${e.sequence_num} carries no met verdict`);
  }
  return {
    kind: 'verdict',
    met: e.data.met,
    critiquePreview: e.data.critique_preview as string | undefined,
  };
}

export function extractRunPasses(
  events: readonly SessionEvent[],
  run: Pick<ProgressTree, 'startedAt' | 'endedAt' | 'runId'>,
  nextStartedAt?: string | null,
): ProcessPassGroup[] {
  const ownedByRun = (e: SessionEvent): boolean => run.runId !== '' && e.data.run_id === run.runId;
  const inWindow = (ts: string): boolean => {
    if (ts < run.startedAt) return false;
    if (run.endedAt != null) return ts <= run.endedAt;
    // An open run must not absorb the next turn's process events.
    if (nextStartedAt != null) return ts < nextStartedAt;
    return true;
  };

  const byPass = new Map<number, ProcessPassGroup>();

  const sorted = [...events].sort((a, b) => a.sequence_num - b.sequence_num);
  for (const e of sorted) {
    const isCheck = e.event_type === 'process_evaluation' && ownedByRun(e);
    if (!isCheck && !inWindow(e.created_at)) continue;
    switch (e.event_type) {
      case 'process_pass_started': {
        const pass = (e.data.pass as number) ?? byPass.size + 1;
        if (!byPass.has(pass)) {
          byPass.set(pass, { pass, startedAt: e.created_at });
        }
        break;
      }
      case 'process_evaluation': {
        const pass = (e.data.pass as number) ?? byPass.size;
        const group = byPass.get(pass);
        if (group) group.evaluation = passEvaluation(e);
        break;
      }
    }
  }

  return [...byPass.values()].sort((a, b) => a.pass - b.pass);
}

export interface PassPartition {
  group: ProcessPassGroup;
  steps: ProgressStep[];
  rootNodes: ProgressNode[];
}

export function partitionRunByPass(run: ProgressTree, groups: ProcessPassGroup[]): PassPartition[] {
  const partitions: PassPartition[] = groups.map((group) => ({
    group,
    steps: [],
    rootNodes: [],
  }));

  const bucketFor = (ts: string): PassPartition => {
    let selected = partitions[0];
    for (const partition of partitions) {
      if (partition.group.startedAt <= ts) selected = partition;
      else break;
    }
    return selected;
  };

  for (const step of run.steps) {
    bucketFor(step.timestamp).steps.push(step);
  }

  const byId = new Map(run.nodes.map((n) => [n.id, n]));
  for (const node of run.nodes) {
    if (!node.parentId || !byId.has(node.parentId)) {
      bucketFor(node.startedAt).rootNodes.push(node);
    }
  }

  return partitions;
}
